import { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import config from '../config';

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const { token, user } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const isConnectingRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const navigate = useNavigate();

  const MAX_RECONNECT_ATTEMPTS = 5;

  const handleMessage = useCallback((event) => {
    try {
      // Ignore non-JSON keepalive messages like "pong"
      if (event.data === 'pong') {
        return;
      }
      const data = JSON.parse(event.data);
      if (data.type === 'new_comment') {
        toast.info(
          <div 
            className="cursor-pointer" 
            onClick={() => {
              navigate(`/user-stories/${data.story_id}`);
              toast.dismiss();
            }}
          >
            <p className="font-medium">New comment on {data.issue_number}</p>
            <p className="text-sm text-muted-foreground">
              {data.commenter_name}: {data.comment_preview}
            </p>
            <p className="text-xs text-primary mt-1">Click to view</p>
          </div>,
          {
            duration: 10000,
            action: {
              label: 'View',
              onClick: () => navigate(`/user-stories/${data.story_id}`)
            }
          }
        );
      } else if (data.type === 'test_notification') {
        toast.info(
          <div>
            <p className="font-medium">Test Notification</p>
            <p className="text-sm text-muted-foreground">{data.message}</p>
            <p className="text-xs text-muted-foreground mt-1">Sent by: {data.sent_by}</p>
          </div>,
          { duration: 8000 }
        );
      }
    } catch (e) {
      console.error('Failed to parse WebSocket message:', e);
    }
  }, [navigate]);

  useEffect(() => {
    // Don't connect if not authenticated
    if (!token || !user) {
      if (wsRef.current) {
        wsRef.current.close(1000, 'User logged out');
        wsRef.current = null;
      }
      return;
    }

    // New token/user (e.g. a fresh login) gets a fresh set of retries
    reconnectAttemptsRef.current = 0;

    const connect = () => {
      // Prevent multiple simultaneous connection attempts
      if (isConnectingRef.current) {
        return;
      }
      
      // Don't reconnect if already connected
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        return;
      }

      // Clean up any existing connection
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      isConnectingRef.current = true;

      try {
        const backendUrl = config.BACKEND_URL;
        const wsUrl = backendUrl.replace('http', 'ws');
        const ws = new WebSocket(`${wsUrl}/api/ws/notifications?token=${token}`);

        ws.onopen = () => {
          console.log('WebSocket connected');
          isConnectingRef.current = false;
          reconnectAttemptsRef.current = 0;

          // Start ping interval to keep connection alive
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
          }
          pingIntervalRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send('ping');
            }
          }, 25000); // Ping every 25 seconds
        };

        ws.onmessage = handleMessage;

        ws.onclose = (event) => {
          console.log('WebSocket disconnected:', event.code, event.reason);
          isConnectingRef.current = false;
          wsRef.current = null;
          
          // Clear ping interval
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }
          
          // Reconnect (with backoff) if not a normal closure, still have a
          // token, and haven't exhausted retries - a persistently failing
          // connection (e.g. an expired token) would otherwise retry every
          // 3 seconds forever.
          if (event.code !== 1000 && token) {
            if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
              console.error(`WebSocket: giving up after ${MAX_RECONNECT_ATTEMPTS} failed reconnect attempts`);
              return;
            }
            reconnectAttemptsRef.current += 1;
            const delay = Math.min(3000 * 2 ** (reconnectAttemptsRef.current - 1), 30000);

            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
            }
            reconnectTimeoutRef.current = setTimeout(() => {
              console.log(`Attempting to reconnect WebSocket... (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`);
              connect();
            }, delay);
          }
        };

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          isConnectingRef.current = false;
        };

        wsRef.current = ws;

      } catch (error) {
        console.error('Failed to connect WebSocket:', error);
        isConnectingRef.current = false;
      }
    };

    // Connect after a short delay
    const connectTimeout = setTimeout(connect, 1000);

    return () => {
      clearTimeout(connectTimeout);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      // Don't close on cleanup - let it stay connected across re-renders
      // Only close when token/user becomes null (handled at top of effect)
    };
  }, [token, user, handleMessage]);

  return (
    <NotificationContext.Provider value={{}}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
